import { useMediaQuery } from 'beautiful-react-hooks';
import type { MapMouseMoveEvent } from 'components/map/hooks/use-mouse-event';
import { useMapMouseMoveEvent } from 'components/map/hooks/use-mouse-event';
import { addImagesToMap, mapMarkerImages } from 'components/map/markers-images.component';
import MapboxUtil from 'components/map/sd-mapbox.util';
import { MEDIA_QUERIES } from 'config/style/breakpoints';
import type { Feature, FeatureCollection, GeoJsonProperties, Point, Polygon } from 'geojson';
import mapboxgl from 'mapbox-gl';
import type {
  RegionalOverviewPolygonFeature,
  RegionalOverviewV2Properties,
  RegionalOverviewV2Values
} from 'querys/regional-overview/regional-overview.query.models';
import { RegionalOverviewSource } from 'querys/regional-overview/regional-overview.query.models';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { renderToString } from 'react-dom/server';
import { MapDetailSheet } from '../regional-risk-map/map-detail-sheet.component';
import RegionalRiskPopupProperty from '../regional-risk-map/regional-risk-popup-property';
import RegionalRiskPopup from '../regional-risk-map/regional-risk-popup.component';
import type { MapPropertiesPointFeature } from '../regional-risk.models';
import { getPropertiesMarkers } from '../regional-risk.utils';
import { getRiskColor, isModelEnabled } from './regional-overview-map-utils';
import { useClusterPopup } from './use-cluster-popup.hook';
import { useOverviewPointsCluster } from './use-overview-points-cluster.hook';
import { useOverviewPointsPopup } from './use-overview-points-popup.hook';

interface RenderRegionalRiskMapProps<T extends GeoJsonProperties> {
  map?: mapboxgl.Map;
  mapPolygonFeatures?: Feature<Polygon, T>[];
  mapPointFeatures?: Feature<Point, T>[];
  model: RegionalOverviewSource;
}

interface UseRenderMapDiseaseRiskFieldReturn {
  portalContent: ReactNode;
}

export const DATA_SOURCE_POINTS = 'data-source-points';
export const OVERVIEW_POINTS_LAYER = 'data-overview-points-layer';
export const OVERVIEW_POINTS_HIGHLIGHT_LAYER = 'data-overview-points-highlight-layer';
export const DATA_SOURCE_POLYGONS = 'data-source-polygons';
export const FIELD_LAYER = 'data-field-layer';
export const FIELD_LINE_LAYER = 'data-field-line-layer';
export const DATA_SOURCE_PROPERTY_POINTS = 'data-source-property-points';
export const POINTS_PROPERTY_LAYER = 'property-point-layer';

const IMAGES = mapMarkerImages.filter(image => image.id === 'propertyRegionalRisk');

export function useRenderMapDiseaseRiskField<T extends GeoJsonProperties>({
  map,
  mapPolygonFeatures,
  mapPointFeatures,
  model
}: RenderRegionalRiskMapProps<T>): UseRenderMapDiseaseRiskFieldReturn {
  const propertiesData = useMemo<FeatureCollection<Point, MapPropertiesPointFeature>>(() => {
    return getPropertiesMarkers((mapPolygonFeatures as unknown as RegionalOverviewPolygonFeature[]) ?? []);
  }, [mapPolygonFeatures]);

  const addImages = useCallback(() => {
    if (!map) return;
    if (IMAGES.length) {
      addImagesToMap(map, IMAGES);
    }
  }, [map]);

  useEffect(() => {
    if (!isModelEnabled(model, RegionalOverviewSource.DISEASE_RISK) || !map) return () => undefined;

    addImages();
    map.addSource(DATA_SOURCE_POLYGONS, {
      type: 'geojson',
      cluster: false,
      data: {
        type: 'FeatureCollection',
        features: []
      }
    });

    map.addSource(DATA_SOURCE_POINTS, {
      type: 'geojson',
      cluster: false,
      generateId: true,
      data: {
        type: 'FeatureCollection',
        features: []
      }
    });

    map.addSource(DATA_SOURCE_PROPERTY_POINTS, {
      type: 'geojson',
      cluster: false,
      data: {
        type: 'FeatureCollection',
        features: []
      },
      clusterMaxZoom: 15,
      clusterRadius: 1,
      maxzoom: 23
    });

    map.addLayer({
      id: FIELD_LAYER,
      type: 'fill',
      minzoom: 10,
      source: DATA_SOURCE_POLYGONS,
      paint: {
        'fill-color': getRiskColor(),
        'fill-opacity': 0.7
      }
    });

    map.addLayer({
      id: FIELD_LINE_LAYER,
      type: 'line',
      minzoom: 10,
      source: DATA_SOURCE_POLYGONS,
      paint: {
        'line-color': getRiskColor(),
        'line-opacity': 0.7
      }
    });

    map.addLayer({
      id: POINTS_PROPERTY_LAYER,
      type: 'symbol',
      source: DATA_SOURCE_PROPERTY_POINTS,
      minzoom: 10,
      layout: {
        'icon-image': ['get', 'icon'],
        'icon-offset': ['coalesce', ['get', 'icon_offset'], ['literal', [0, -26]]],
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'text-allow-overlap': true,
        'text-field': ['get', 'description'],
        'text-font': ['Open Sans Regular'],
        'text-size': 16,
        'text-offset': [0, -2]
      },
      paint: {
        'text-color': '#000000',
        'icon-opacity': ['interpolate', ['linear'], ['zoom'], 10, 0, 11, 1]
      }
    });

    return () => {
      MapboxUtil.removeLayersIfExists(map, [FIELD_LAYER, FIELD_LINE_LAYER, POINTS_PROPERTY_LAYER]);
      MapboxUtil.removeSourcesIfExists(map, [DATA_SOURCE_POLYGONS, DATA_SOURCE_POINTS, DATA_SOURCE_PROPERTY_POINTS]);
      MapboxUtil.removeImagesIfExists(
        map,
        IMAGES.map(image => image.id)
      );
    };
  }, [map, addImages, model]);

  const handleStyleLoad = useCallback(() => {
    addImages();
  }, [addImages]);

  useEffect(() => {
    if (!isModelEnabled(model, RegionalOverviewSource.DISEASE_RISK) || !map) return () => undefined;

    map.on('style.load', handleStyleLoad);

    return () => {
      map.off('style.load', handleStyleLoad);
    };
  }, [map, handleStyleLoad, model]);

  useEffect(() => {
    if (!isModelEnabled(model, RegionalOverviewSource.DISEASE_RISK) || !map || !mapPolygonFeatures || !mapPointFeatures) return;

    const polygonSource = map.getSource(DATA_SOURCE_POLYGONS) as mapboxgl.GeoJSONSource;
    const pointSource = map.getSource(DATA_SOURCE_POINTS) as mapboxgl.GeoJSONSource;

    if (polygonSource && pointSource) {
      polygonSource.setData({ type: 'FeatureCollection', features: mapPolygonFeatures });
      pointSource.setData({ type: 'FeatureCollection', features: mapPointFeatures });
    }
  }, [map, mapPointFeatures, mapPolygonFeatures, model]);

  useEffect(() => {
    if (!isModelEnabled(model, RegionalOverviewSource.DISEASE_RISK) || !map || !propertiesData) return;
    const source = map.getSource(DATA_SOURCE_PROPERTY_POINTS);
    (source as mapboxgl.GeoJSONSource).setData(propertiesData);
  }, [map, model, propertiesData]);

  const [mapPopup] = useState(
    new mapboxgl.Popup({
      closeButton: false,
      offset: [20, 20],
      closeOnClick: false
    })
  );

  const onMouseMove = useCallback(
    (e: MapMouseMoveEvent) => {
      if (!isModelEnabled(model, RegionalOverviewSource.DISEASE_RISK) || !map) return;
      if (!e.features) return;
      const feature = e.features[0];
      const coordinates = e.lngLat;
      if (!feature?.properties) return;

      if (feature?.layer?.id === POINTS_PROPERTY_LAYER) {
        mapPopup
          .setLngLat(coordinates)
          .setHTML(renderToString(<RegionalRiskPopupProperty properties={feature.properties as MapPropertiesPointFeature} />))
          .addTo(map);
      } else {
        const loadedProperties = {
          ...(feature.properties as RegionalOverviewV2Properties),
          values: JSON.parse(feature.properties.values as string) as RegionalOverviewV2Values
        } as RegionalOverviewV2Properties;
        mapPopup
          .setLngLat(coordinates)
          .setHTML(renderToString(<RegionalRiskPopup properties={loadedProperties} />))
          .addTo(map);
      }
    },
    [model, map, mapPopup]
  );

  const onMouseLeave = useCallback(() => {
    mapPopup.remove();
  }, [mapPopup]);

  useMapMouseMoveEvent({ map, labelName: POINTS_PROPERTY_LAYER, onMouseMove, onMouseLeave });

  const isMobile = useMediaQuery(MEDIA_QUERIES.MOBILE);

  const {
    portalContent: pointsPopupContent,
    openFieldPopup,
    isOpen: fieldOpen,
    content: fieldContent,
    onClose: onCloseField
  } = useOverviewPointsPopup({ map, mapPointFeatures, isMobile });
  const {
    portalContent: clusterPopupContent,
    isOpen: clusterOpen,
    content: clusterContent,
    onClose: onCloseCluster,
    onClusterClick
  } = useClusterPopup({ map, onFieldSelect: openFieldPopup, mapPointFeatures, isMobile });

  useOverviewPointsCluster({ map, model, mapPointFeatures, onClusterClick });

  return {
    portalContent: isMobile ? (
      <MapDetailSheet
        fieldOpen={fieldOpen}
        clusterOpen={clusterOpen}
        fieldContent={fieldContent}
        clusterContent={clusterContent}
        onCloseField={onCloseField}
        onCloseCluster={onCloseCluster}
      />
    ) : (
      <>
        {pointsPopupContent}
        {clusterPopupContent}
      </>
    )
  };
}
